import { Injectable } from "@angular/core";
import { environment } from "@edp-environments/environment";
import { getContract } from "@edp-store/selectors/contract.selectors";
import { getUser } from "@edp-store/selectors/user.selectors";
import { IAppState } from "@edp-store/state/app.state";
import { select, Store } from "@ngrx/store";
import { GoogleTagManagerService } from "angular-google-tag-manager";
import { combineLatest } from "rxjs";
import datalayerConfigs from "../../assets/jsons/datalayerConfigs.json";
import { GaClick, GaEvent } from "@edp-models/datalayer.model";
import { TranslateService } from "@ngx-translate/core";

@Injectable({
    providedIn: "root",
})
export class DatalayerService { 

    private canSetPageView = false;

    constructor(
        private readonly store: Store<IAppState>,
        public google: GoogleTagManagerService,
        public translateService: TranslateService
    ) {
    }

    private extractKgValue(value: string): string {
        if (typeof value === 'string') {
            const match = value.match(/(\d+kg)/i); 
            return match ? match[0] : value;
        }
        return value;
    }
    
    public setGaEvent(type: string, extraData?: any) {
        const gaEvent = {
            ...{
                event: "gaevent",
                dlVersion: "GA4"
            },
            ...datalayerConfigs[type],
            ...extraData
        } as GaEvent;

        this.google.pushTag(gaEvent);
    }

    public setGaClick(type: string, extraData?: any) {
        const gaClick = {
            ...{
                event: "gaclick",
                dlVersion: "GA4"
            },
            ...datalayerConfigs[type],
            ...extraData
        } as GaClick;

        this.google.pushTag(gaClick);
    }

    public pageViewEvent(url: string, isPageView: boolean) {
        this.canSetPageView = !!isPageView ? false : true;
        combineLatest([
            this.store.pipe(select(getUser)),
            this.store.pipe(select(getContract)),
        ]).subscribe(([user, contract]) => {
            if (user && contract && !this.canSetPageView) {
                this.canSetPageView = true;
                const payload = {
                    userId: user.uid,
                    pageCategory: url,
                    pageName: url,
                    source: environment.isApp ? "app" : "web",
                    clientInfo: {
                        Planeta: user.edpESubscribed ? 1 : 0,
                        CGas: contract.HasG ? 1 : 0,
                        CSolar: contract.Product.find(el => el.ProductCodes.find(el2 => el2.includes("REDY") || el2.includes("SOLAR")))
                            ? 1 : 0,
                        CPackLivingEDP: contract.HasPack ? 1 : 0,
                        CFE: contract.HasElectronicInvoice ? 1 : 0,
                        CCartaoMobilidade: contract.Product.find(el => el.ProductCodes.find(el2 => el2.includes("MOBIE"))) ? 1 : 0,
                        CPlanoEDPSaude: contract.Product.find(el => el.ProductCodes.find(el2 => el2.includes("SENIOR"))) ? 1 : 0,
                        CContaCerta: contract.HasContaCerta ? 1 : 0,
                        CDirectDebit: contract.HasDirectDebit ? 1 : 0,
                        CFaturaSegura: contract.HasServiceFaturaSegura ? 1 : 0
                    }
                };

                this.setGaEvent("page", payload);
            }
        });
    }

    public mapperServiceAttributes(payloadContractDefineHouse: any, objectFormConfig: any, MappingKeyGAReference: any) {
        const result = {
            serviceAttributes: {}
        };

        const properties = payloadContractDefineHouse?.properties;

        if (!Array.isArray(properties)) {
            result.serviceAttributes[properties.queryCode] = properties.data;
            return result;
        }

        payloadContractDefineHouse?.properties?.forEach(({ queryCode, data: value }) => {
            if (MappingKeyGAReference[queryCode]) {
                const refItem = objectFormConfig.find(({ key }) => key === queryCode);

                if (refItem) {
                    if (refItem.options) {

                        if(typeof value === 'number') {
                            result.serviceAttributes[MappingKeyGAReference[queryCode]] = value;
                        }
                        
                        if(typeof value === 'string') {
                            const optionLabels = value.split(", ").map(val => {
                                const option = refItem.options.find(opt => opt.value === val);
                                const translatedValue = option ? this.translateService.instant(option.label) : val;
                                return this.extractKgValue(translatedValue);
                            });
                            result.serviceAttributes[MappingKeyGAReference[queryCode]] = optionLabels.join(", ");
                        }
                        
                    } else {
                        result.serviceAttributes[MappingKeyGAReference[queryCode]] = value;
                    }
                }
            }
        });

        return result;
    }
}
